import { CommerceService } from "@/services/commerce"

const commerceApi = CommerceService.api()

/**
 * Method to retrieve product listing knowing variants and branch key
 *
 * @param {array} variants
 * @param {string|null} branchKey
 *
 * @return {Promise<array>|null}
 */
export const getListingByBranch = async ({ variants, branchKey, limit = 500 }) => {
  // in case one is missing, nothing can be done, no variants or branch
  if (!variants || variants.length <= 0 || !branchKey) return null

  // get branch info, more particularly, the id of the channel which is treated as branch
  // this is to reduce the amount of results once listings are retrieved
  const branchInfo = (
    await commerceApi
      .channels()
      .get({
        queryArgs: {
          where: `key="NL-${branchKey}"`,
        },
      })
      .execute()
  )?.body?.results

  // if branch was not found, something is wrong
  if (!branchInfo || branchInfo.length <= 0) return null

  let skus = variants.map((variant) => {
    if (!variant?.sku) return null
    return variant.sku
  })

  skus = skus.filter((n) => n)

  const stockKeysWithSkus = await getStockKeys({ skus })

  const stockKeys = stockKeysWithSkus.map((stockKey) => `"${stockKey?.stockKey}"`).join(", ")

  const branchId = branchInfo[0].id

  const listings = (
    await commerceApi
      .inventory()
      .get({
        queryArgs: {
          limit,
          where: `sku in (${stockKeys}) and supplyChannel(id="${branchId}")`,
        },
      })
      .execute()
  )?.body.results

  if (listings.length <= 0) return null

  return stockKeysWithSkus.map((stockKeyWithSku) => {
    let availability = "unknown"
    let chargenData = []

    const listing = listings.find((listing) => listing?.sku === stockKeyWithSku?.stockKey)

    if (!listing) {
      return {
        listing: null,
        availability,
        chargenData,
        variantSku: stockKeyWithSku?.sku,
      }
    }

    const customFields = listing?.custom?.fields

    if (customFields?.chargeData?.length > 0) {
      chargenData = customFields?.chargeData
    }

    if (!customFields) {
      return {
        ...listing,
        availability,
      }
    }

    if (customFields?.adm_availableQuantity > 0) {
      availability = "available"
    }

    if (customFields?.adm_availableQuantity <= 0) {
      availability = "unavailable"
    }

    return {
      ...listing,
      availability,
      chargenData,
      variantSku: stockKeyWithSku?.sku,
    }
  })
}

/**
 * Takes sku strings and returns stock keys in already prepared commercetools query format
 *
 * @param {{skus: array}}
 *
 * @returns {Promise<string>}
 */
export const getStockKeys = async ({ skus, limit = 500 }) => {
  if (!skus || skus.length <= 0) return []

  const skuList = skus.map((sku) => `"${sku}"`)
  const skuString = skuList.join(", ")

  // need to retrieve "stock-key" which is stored in commercetools articles
  // masterVariant and variants are separated into 2 different parts
  const commerceToolsVariants = (
    await commerceApi
      .productProjections()
      .get({
        queryArgs: {
          limit,
          where: `masterVariant(sku in (${skuString})) or variants(key in (${skuString}))`,
        },
      })
      .execute()
  )?.body?.results

  let ctVariants = []

  if (commerceToolsVariants.length === 0) return skuList

  commerceToolsVariants.map((ctProduct) => {
    const ctProductMasterVariant = ctProduct?.masterVariant ? [ctProduct.masterVariant] : []
    const ctProductVariants = ctProduct?.variants || []

    // store everything into 1 array
    ctVariants = [...ctVariants, ...ctProductMasterVariant, ...ctProductVariants]

    return true
  })

  if (ctVariants.length === 0) return skuList

  let stockKeys = ctVariants.map((ctVariant) => {
    if (!ctVariant || ctVariant?.attributes?.length === 0) return null

    // commercetools returns all variants from a product even if specified variants were queried
    if (!skus.includes(ctVariant?.sku)) return null

    const stockKeyObject = ctVariant?.attributes?.find((attribute) => attribute?.name === "stock-key")

    // value is not set, use sku instead
    if (!stockKeyObject) return `"${ctVariant?.sku}"`

    return { sku: ctVariant.sku, stockKey: stockKeyObject?.value }
  })

  stockKeys = stockKeys.filter((n) => n)

  if (stockKeys.length === 0) return skuList

  return stockKeys
}
